import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  SignedIn,
  SignedOut,
  SignIn,
  UserButton,
  useAuth,
  useUser
} from '@clerk/clerk-react';
import api, { extractApiError, isRequestCanceled, unwrapApiResponse } from '../api/api';
import QueryBuilder from '../components/QueryBuilder';
import DataTable from '../components/DataTable';
import ReportsView from '../components/ReportsView';
import IngestionView from '../components/IngestionView';
import OperationsMonitorView from '../components/OperationsMonitorView';
import ObservabilityView from '../components/ObservabilityView';
import TenantCleanupView from '../components/TenantCleanupView';
import UserAdminView from '../components/UserAdminView';
import { useToast } from '../components/ToastProvider';

const FALLBACK_FILTERS = {
  table: 'data_tnfe',
  limit: 50,
  orderBy: 'TOTAL',
  tipoPersona: 'FISICA'
};

const VIEW_TITLES = {
  dashboard: 'Dashboard Analytics',
  reports: 'Reportes ejecutivos',
  ingestion: 'Ingesta masiva',
  monitor: 'Monitoreo operativo',
  stages: 'Observabilidad',
  cleanup: 'Limpieza por tenant'
};

export default function App() {
  const { showError } = useToast();
  const { user, isLoaded: userLoaded } = useUser();
  const { getToken, isLoaded: authLoaded } = useAuth();

  const [view, setView] = useState('reports');
  const [filters, setFilters] = useState(FALLBACK_FILTERS);
  const [data, setData] = useState([]);
  const [status, setStatus] = useState({ loading: false, error: '' });
  const [metadata, setMetadata] = useState(null);
  const requestRef = useRef(null);
  const permissionToastRef = useRef('');
  const roleViewInitializedRef = useRef(false);

  const role = user?.publicMetadata?.role || null;
  const isMaster = user?.publicMetadata?.isMaster === true || user?.publicMetadata?.isMaster === 'true';
  const canAccessReports = role === 'admin' || role === 'reports_reader';
  const canAccessIngestion = role === 'admin' || role === 'ingestion_operator';
  const canAccessMonitor = canAccessIngestion;
  const canAccessStages = role === 'admin' && isMaster ;
  const canAccessCleanup = role === 'admin';
  const canAccessDashboard = role === 'admin';
  const canAccessUserAdmin = role === 'admin';

  async function loadMetadata(signal) {
    const token = await getToken({ skipCache: true });
    const response = await api.get('/query/metadata', {
      headers: { Authorization: `Bearer ${token}` },
      signal
    });
    const nextMetadata = unwrapApiResponse(response);
    setMetadata(nextMetadata);
    if (nextMetadata?.dashboard?.defaultFilters) {
      setFilters((current) => ({ ...current, ...nextMetadata.dashboard.defaultFilters }));
    }
    return nextMetadata;
  }

  useEffect(() => {
    if (!authLoaded || !userLoaded || !canAccessReports) return undefined;

    const controller = new AbortController();
    loadMetadata(controller.signal).catch((error) => {
      if (!isRequestCanceled(error)) {
        const message = extractApiError(error, 'No se pudo cargar la metadata');
        setStatus((prev) => ({ ...prev, error: message }));
        showError(message);
      }
    });

    return () => controller.abort();
  }, [authLoaded, canAccessReports, getToken, showError, userLoaded]);

  const tableOptions = metadata?.dashboard?.tableOptions || [];
  const currentTableMeta = tableOptions.find((item) => item.value === filters.table);


  const title = useMemo(() => {
    if (view !== 'dashboard') return VIEW_TITLES[view] || 'Usuarios y entidades';
    return currentTableMeta?.title || `Top clientes ${filters.table}`;
  }, [currentTableMeta?.title, filters.table, view]);

  const defaultView = useMemo(() => {
    if (role === 'admin' || role === 'reports_reader') return 'reports';
    if (role === 'ingestion_operator') return 'ingestion';
    return 'reports';
  }, [role]);

  const allowedViews = useMemo(() => {
    const allowed = [];
    if (canAccessDashboard) allowed.push('dashboard');
    if (canAccessReports) allowed.push('reports');
    if (canAccessIngestion) allowed.push('ingestion');
    if (canAccessMonitor) allowed.push('monitor');
    if (canAccessStages) allowed.push('stages');
    if (canAccessCleanup) allowed.push('cleanup');
    if (canAccessUserAdmin) allowed.push('users');
    return allowed;
  }, [canAccessCleanup, canAccessDashboard, canAccessIngestion, canAccessMonitor, canAccessReports, canAccessStages, canAccessUserAdmin]);

  const canAccessView = useMemo(() => ({
    dashboard: canAccessDashboard,
    reports: canAccessReports,
    ingestion: canAccessIngestion,
    monitor: canAccessMonitor,
    stages: canAccessStages,
    cleanup: canAccessCleanup,
    users: canAccessUserAdmin
  }), [canAccessCleanup, canAccessDashboard, canAccessIngestion, canAccessMonitor, canAccessReports, canAccessStages, canAccessUserAdmin]);

  const changeView = useMemo(() => (nextView) => {
    if (!canAccessView[nextView]) {
      const message = 'No tienes permisos para acceder a esa pantalla.';
      if (permissionToastRef.current !== `${nextView}:${role}`) {
        showError(message);
        permissionToastRef.current = `${nextView}:${role}`;
      }
      setView(defaultView);
      return;
    }
    permissionToastRef.current = '';
    setView(nextView);
  }, [canAccessView, defaultView, role, showError]);



  useEffect(() => {
    if (!authLoaded || !userLoaded) return;
    if (!allowedViews.length) return;
    if (!roleViewInitializedRef.current) {
      roleViewInitializedRef.current = true;
      if (!canAccessView[view]) {
        setView(defaultView);
      }
      return;
    }

    if (!canAccessView[view]) {
      const blockedView = view;
      setView(defaultView);
      if (blockedView !== defaultView && permissionToastRef.current !== `${blockedView}:${role}`) {
        showError('No tienes permisos para acceder a esa pantalla.');
        permissionToastRef.current = `${blockedView}:${role}`;
      }
    } else {
      permissionToastRef.current = '';
    }
  }, [allowedViews.length, authLoaded, canAccessView, defaultView, role, showError, userLoaded, view]);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
    if (key === 'table') setData([]);
  }

  async function runQuery() {
    if (!canAccessDashboard) {
      changeView(defaultView);
      return;
    }
    requestRef.current?.abort?.();
    const controller = new AbortController();
    requestRef.current = controller;

    try {
      setStatus({ loading: true, error: '' });
      const token = await getToken({ skipCache: true });
      const response = await api.get('/query/top', {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          table: filters.table,
          limit: filters.limit,
          orderBy: filters.orderBy,
          tipoPersona: filters.tipoPersona
        },
        signal: controller.signal
      });

      setData(unwrapApiResponse(response) || []);
      setStatus({ loading: false, error: '' });
    } catch (error) {
      if (isRequestCanceled(error)) return;
      const message = extractApiError(error, 'Error al ejecutar la consulta');
      setStatus({
        loading: false,
        error: message
      });
      showError(message);
    } finally {
      if (requestRef.current === controller) {
        requestRef.current = null;
      }
    }
  }

  useEffect(() => () => requestRef.current?.abort?.(), []);

  if (!userLoaded || !authLoaded) {
    return null;
  }

  return (
    <>
      <SignedOut>
        <main className="login-screen">
          <section className="login-card">
            <div className="brand-badge">ClickHouse</div>
            <h1>Dashboard Analytics</h1>
            <p>Inicia sesión para continuar.</p>
            <SignIn
              forceRedirectUrl="/dashboard"
              fallbackRedirectUrl="/dashboard"
              appearance={{ elements: { footer: { display: 'none' } } }}
            />
          </section>
        </main>
      </SignedOut>

      <SignedIn>
        <main className="app-shell report-app-shell">
          <header className="hero-card app-hero-card">
            <div>
              <div className="brand-badge">Analytics</div>
              <h1>{title}</h1>
              <p>Consulta de Rubros y operación de ingesta</p>
            </div>
            <div className="hero-actions app-hero-actions">
              <div className="view-switcher multi-view-switcher responsive-view-switcher">
                {/* {canAccessDashboard ? <button type="button" className={view === 'dashboard' ? 'primary-button' : 'secondary-button'} onClick={() => changeView('dashboard')}>Dashboard</button> : null} */}
                {canAccessReports ? <button type="button" className={view === 'reports' ? 'primary-button' : 'secondary-button'} onClick={() => changeView('reports')}>Reportes</button> : null}
                {canAccessIngestion ? <button type="button" className={view === 'ingestion' ? 'primary-button' : 'secondary-button'} onClick={() => changeView('ingestion')}>Ingesta</button> : null}
                {canAccessMonitor ? <button type="button" className={view === 'monitor' ? 'primary-button' : 'secondary-button'} onClick={() => changeView('monitor')}>Monitoreo</button> : null}
                {canAccessUserAdmin ? <button type="button" className={view === 'users' ? 'primary-button' : 'secondary-button'} onClick={() => changeView('users')}>Usuarios</button> : null}
                {/* {canAccessTechnical ? <button type="button" className={view === 'technical' ? 'primary-button' : 'secondary-button'} onClick={() => changeView('technical')}>Técnico</button> : null} */}
                {canAccessStages ? <button type="button" className={view === 'stages' ? 'primary-button' : 'secondary-button'} onClick={() => changeView('stages')}>Observabilidad</button> : null}
                {canAccessCleanup ? <button type="button" className={view === 'cleanup' ? 'primary-button' : 'secondary-button'} onClick={() => changeView('cleanup')}>Limpieza</button> : null}
              </div>
              <UserButton />
            </div>
          </header>

          {!allowedViews.length ? (
            <section className="table-card">
              <p className="error-text">No autorizado.</p>
            </section>
          ) : view === 'reports' ? (
            <ReportsView getToken={getToken} metadata={metadata?.reports} />
          ) : view === 'ingestion' ? (
            <IngestionView getToken={getToken} onJobCreated={canAccessReports ? () => loadMetadata() : undefined} />
          ) : view === 'monitor' ? (
            <OperationsMonitorView getToken={getToken} />
          ) : view === 'users' ? (
            <UserAdminView getToken={getToken} tenantOptions={metadata?.reports?.tenantOptions || []} />
          ) : view === 'stages' ? (
            <ObservabilityView metadata={metadata?.observability} />
          ) : view === 'cleanup' ? (
            <TenantCleanupView getToken={getToken} metadata={metadata?.cleanup} />
          ) : (
            <>
              <QueryBuilder
                filters={filters}
                metadata={metadata?.dashboard}
                onChange={updateFilter}
                onRun={runQuery}
                disabled={status.loading}
              />
              {status.loading ? (
                <div className="screen-loader">
                  <div className="loader-card">
                    <div className="spinner" />
                    <p>Cargando datos...</p>
                  </div>
                </div>
              ) : null}

              <DataTable data={data} table={filters.table} />
            </>
          )}
        </main>
      </SignedIn>
    </>
  );
}
